import { useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/useEntity';

type FxStatus = {
  valor: number | null;
  actualizado_at: string | null;
  fuente: string | null;
};

type FxRefreshResponse = {
  clave: string;
  valor: number;
  actualizado_at: string;
  fecha_ref: string;
  fuente: string;
};

function relativeTime(iso: string | null): string {
  if (!iso) return 'nunca';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return 'nunca';
  const diffSec = Math.round((Date.now() - then) / 1000);
  if (diffSec < 60) return `hace ${diffSec}s`;
  if (diffSec < 3600) return `hace ${Math.round(diffSec / 60)} min`;
  if (diffSec < 86400) return `hace ${Math.round(diffSec / 3600)} h`;
  return `hace ${Math.round(diffSec / 86400)} d`;
}

export function FxWidget() {
  const qc = useQueryClient();
  const status = useQuery({
    queryKey: ['fx-status'],
    queryFn: () => api.get<FxStatus>('/parametros/_fx/status'),
    staleTime: 60_000,
  });

  const refresh = useMutation({
    mutationFn: () => api.post<FxRefreshResponse>('/parametros/refresh-fx', {}),
    onSuccess: (data) => {
      qc.setQueryData<FxStatus>(['fx-status'], {
        valor: data.valor,
        actualizado_at: data.actualizado_at,
        fuente: data.fuente,
      });
      qc.invalidateQueries({ queryKey: ['list', '/admin/parametros'] });
    },
  });

  // Auto-refresh al iniciar la sesión: si el último update tiene más de 12h
  // (o nunca se hizo), lanza un refresh silencioso. Una sola vez por carga
  // del Layout para no spamear.
  const ranAutoRef = useRef(false);
  useEffect(() => {
    if (ranAutoRef.current) return;
    if (status.isLoading || !status.data) return;
    ranAutoRef.current = true;
    const last = status.data.actualizado_at ? new Date(status.data.actualizado_at).getTime() : 0;
    const ageHours = (Date.now() - last) / 3_600_000;
    if (!status.data.valor || ageHours >= 12) {
      refresh.mutate();
    }
  }, [status.isLoading, status.data, refresh]);

  const valor = refresh.data?.valor ?? status.data?.valor;
  const updatedAt = refresh.data?.actualizado_at ?? status.data?.actualizado_at ?? null;
  const isBusy = status.isLoading || refresh.isPending;
  const isError = refresh.isError;

  return (
    <div className="flex items-center gap-2 rounded-md border border-slate-200 bg-white px-2.5 py-1 text-xs">
      <span className="font-medium text-slate-500">USD→EUR</span>
      <span className="font-mono font-semibold text-slate-900">
        {valor != null ? valor.toFixed(4) : '—'}
      </span>
      <span className="hidden text-slate-400 sm:inline" title={updatedAt ?? undefined}>
        {relativeTime(updatedAt)}
      </span>
      <button
        type="button"
        onClick={() => refresh.mutate()}
        disabled={isBusy}
        title={isError ? 'Error al actualizar — pulsa para reintentar' : 'Actualizar tipo de cambio'}
        className={`flex h-6 w-6 cursor-pointer items-center justify-center rounded-md transition disabled:cursor-not-allowed disabled:opacity-50 ${
          isError ? 'text-rose-600 hover:bg-rose-50' : 'text-slate-500 hover:bg-slate-100'
        }`}
        aria-label="Actualizar tipo de cambio"
      >
        <svg
          viewBox="0 0 24 24"
          className={`h-4 w-4 ${refresh.isPending ? 'animate-spin' : ''}`}
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M21 12a9 9 0 1 1-3.27-6.94" />
          <polyline points="21 4 21 10 15 10" />
        </svg>
      </button>
    </div>
  );
}
